/**
 * BrandStudioResumeChip — Sprint 2026-06-11 PM-9 Phase 1D (paco).
 *
 * One-click "Resume where you left off" chip surfaced on the Brand Studio
 * landing page. Reads the last leaf sub-page the user visited inside
 * Brand Studio from localStorage (recorded by `recordBrandLastVisited()`
 * which the BrandStudioPillarsBreadcrumb component invokes on every
 * canonical Brand path).
 *
 * Tracks across reloads, no backend required for v1.
 *
 * Renders nothing when:
 *   - no record exists yet (first-time visitor)
 *   - record is older than 30 days (treat as stale)
 *   - record points to the current page (avoid self-link)
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, History } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  BUILD_AND_PROTECT_MISSION,
  BRAND_PILLAR_WORKFLOWS,
} from "../../config/missionRegistry";

const STORAGE_KEY = "brand_studio_last_visited";
const MAX_AGE_DAYS = 30;

export function recordBrandLastVisited(pathname, pillarId, childId) {
  if (!pathname || !pillarId || !childId) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        path: pathname,
        pillar: pillarId,
        child: childId,
        visited_at: new Date().toISOString(),
      }),
    );
  } catch { /* ignore — quota errors, private-mode, etc. */ }
}

function readLastVisited() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.path || !obj?.pillar || !obj?.child) return null;
    const ageDays = (Date.now() - new Date(obj.visited_at).getTime()) / 86400000;
    if (ageDays > MAX_AGE_DAYS) return null;
    return obj;
  } catch {
    return null;
  }
}

function localized(obj, lang) {
  if (!obj) return "";
  return obj[lang] || obj.en || "";
}

export default function BrandStudioResumeChip() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { language, t } = useLanguage();
  const [last, setLast] = useState(null);

  useEffect(() => {
    setLast(readLastVisited());
  }, [pathname]);

  // Sprint 2026-06-12 PM-10.2 (paco): on the studio landing page the
  // Resume chip MUST always render. When no localStorage record exists
  // yet, surface a 'Start with <first stage>' chip so the user has an
  // entry point. The chip never silently hides on the landing page.
  if (!last) {
    const first = BUILD_AND_PROTECT_MISSION.stages[0];
    if (!first) return null;
    const accent = first.color || BUILD_AND_PROTECT_MISSION.color;
    const firstLabel = localized(first.label, language);
    return (
      <button
        onClick={() => navigate(first.page)}
        className="group inline-flex items-center gap-2.5 rounded-full pl-3 pr-4 py-2 text-xs font-semibold border transition-all hover:scale-[1.02] hover:-translate-y-px"
        style={{
          borderColor: `${accent}55`,
          backgroundColor: `${accent}12`,
          color: accent,
          boxShadow: `0 1px 0 ${accent}22, 0 0 0 1px ${accent}22`,
        }}
        data-testid="brand-studio-resume-chip"
        data-mode="empty"
        title={t("Start with the foundation — no recent activity yet")}
      >
        <span
          className="inline-flex w-6 h-6 items-center justify-center rounded-full shrink-0"
          style={{ backgroundColor: `${accent}26`, color: accent }}
        >
          <History className="w-3.5 h-3.5" />
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[9px] uppercase tracking-widest font-bold opacity-70">
            {t("Resume where you left off")}
          </span>
          <span className="text-[12px] font-semibold">
            {t("Start with")} {firstLabel}
          </span>
        </span>
        <ArrowRight className="w-3.5 h-3.5 ml-0.5 transition-transform group-hover:translate-x-0.5" />
      </button>
    );
  }
  if (last.path === pathname) return null; // already there

  const pillarStage = BUILD_AND_PROTECT_MISSION.stages.find((s) => s.id === last.pillar);
  if (!pillarStage) return null;
  const accent = pillarStage.color || BUILD_AND_PROTECT_MISSION.color;
  const pillarLabel = localized(pillarStage.label, language);

  const workflow = BRAND_PILLAR_WORKFLOWS[last.pillar];
  const childItem = workflow?.items?.find((c) => c.id === last.child);
  const childLabel = childItem ? localized(childItem.label, language) : last.child;

  const onResume = () => navigate(last.path);

  return (
    <button
      onClick={onResume}
      className="group inline-flex items-center gap-2.5 rounded-full pl-3 pr-4 py-2 text-xs font-semibold border transition-all hover:scale-[1.02] hover:-translate-y-px"
      style={{
        borderColor: `${accent}55`,
        backgroundColor: `${accent}12`,
        color: accent,
        boxShadow: `0 1px 0 ${accent}22, 0 0 0 1px ${accent}22`,
      }}
      data-testid="brand-studio-resume-chip"
      data-pillar={last.pillar}
      data-child={last.child}
      title={t("Pick up where you left off")}
    >
      <span
        className="inline-flex w-6 h-6 items-center justify-center rounded-full shrink-0"
        style={{ backgroundColor: `${accent}26`, color: accent }}
      >
        <History className="w-3.5 h-3.5" />
      </span>
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[9px] uppercase tracking-widest font-bold opacity-70">
          {t("Resume where you left off")}
        </span>
        <span className="text-[12px] font-semibold">
          {pillarLabel} · {childLabel}
        </span>
      </span>
      <ArrowRight className="w-3.5 h-3.5 ml-0.5 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}
